import { useEffect, useState, useMemo } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';

export interface OkrKeyResult {
  id?: string;
  title: string;
  current: string;
  progress?: number;
  confidence: 'green' | 'yellow' | 'red';
  remarks?: string;
}

export interface OkrObjective {
  id?: string;
  title: string;
  priority: number;
  dependencies: 'high' | 'medium' | 'low';
  /** Link to a parent objective. Format: "<entity-name>#<objective-id>" */
  parentObjRef?: string;
  keyResults: OkrKeyResult[];
}

export interface OkrSpec {
  type: 'okr';
  lifecycle: string;
  owner: string;
  quarter: string;
  parentRef?: string;
  missionStatement?: string;
  objectives: OkrObjective[];
  nextQuarterHighlights?: string[];
}

export type OkrEntity = Omit<Entity, 'spec'> & { spec: OkrSpec };

export interface OkrTreeNode {
  entity: OkrEntity;
  children: OkrTreeNode[];
  depth: number;
}

export interface OkrTree {
  roots: OkrTreeNode[];
  byName: Map<string, OkrTreeNode>;
  /** Maps owner group slug (e.g. "technology") → its OKR tree node */
  byOwner: Map<string, OkrTreeNode>;
}

export interface GroupLead {
  displayName: string;
  picture?: string;
  entityName?: string;
}

export interface UseOkrEntitiesResult {
  loading: boolean;
  error: Error | undefined;
  entities: OkrEntity[];
  tree: OkrTree | undefined;
  quarters: string[];
  selectedQuarter: string;
  setSelectedQuarter: (q: string) => void;
  scopeNodes: OkrTreeNode[];
  selectedScope: string | undefined;
  setSelectedScope: (name: string | undefined) => void;
  groupLeads: Map<string, GroupLead>;
}

export function isAnnualQuarter(quarter: string): boolean {
  return /^FY\d+$/i.test(quarter) || /^\d{4}$/.test(quarter);
}

export function getFiscalYear(quarter: string): string {
  const qMatch = quarter.match(/Q\d+-(\d{4})/);
  if (qMatch) return `FY${qMatch[1].slice(2)}`;
  const fyMatch = quarter.match(/FY(\d{2,4})/i);
  if (fyMatch) return `FY${fyMatch[1].slice(-2)}`;
  const yearMatch = quarter.match(/^(\d{4})$/);
  if (yearMatch) return `FY${yearMatch[1].slice(2)}`;
  return quarter;
}

export function computeProgress(okr: OkrEntity): number {
  const krs = okr.spec.objectives.flatMap(o => o.keyResults).filter(kr => kr.progress !== undefined);
  if (!krs.length) return 0;
  return Math.round(krs.reduce((s, kr) => s + (kr.progress ?? 0), 0) / krs.length);
}

export function countKrs(okr: OkrEntity): number {
  return okr.spec.objectives.reduce((n, o) => n + o.keyResults.length, 0);
}

export function formatQuarter(q: string): string {
  const qMatch = q.match(/Q(\d+)-(\d{4})/);
  if (qMatch) return `Q${qMatch[1]} · FY${qMatch[2].slice(2)}`;
  const fyMatch = q.match(/^FY(\d{2,4})$/i);
  if (fyMatch) return `FY${fyMatch[1].slice(-2)}`;
  const yearMatch = q.match(/^(\d{4})$/);
  if (yearMatch) return `FY${yearMatch[1].slice(2)}`;
  return q;
}

export function ownerGroupSlug(okr: OkrEntity): string {
  return okr.spec.owner.split('/').pop() ?? okr.spec.owner;
}

export function formatGroupName(slug: string): string {
  return slug
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function ownerMatches(okr: OkrEntity, group: Entity): boolean {
  const groupName = group.metadata.name;
  return (
    okr.spec.owner === `group:default/${groupName}` ||
    okr.spec.owner.split('/').pop() === groupName
  );
}

function parseParentName(parentRef: string): string {
  return parentRef.split('/').pop() ?? '';
}

function sortQuarters(quarters: string[]): string[] {
  return [...quarters].sort((a, b) => {
    const toKey = (q: string) => {
      const m = q.match(/Q(\d+)-(\d{4})/);
      if (!m) return 0;
      return parseInt(m[2], 10) * 10 + parseInt(m[1], 10);
    };
    return toKey(a) - toKey(b);
  });
}

function buildTree(entities: OkrEntity[], quarter: string): OkrTree {
  const fy = getFiscalYear(quarter);
  const filtered = entities.filter(e => {
    if (e.spec.quarter === quarter) return true;
    // Annual OKRs (company, BU level) match any quarter in the same fiscal year
    if (isAnnualQuarter(e.spec.quarter) && getFiscalYear(e.spec.quarter) === fy) return true;
    return false;
  });

  const nameToNode = new Map<string, OkrTreeNode>();

  for (const entity of filtered) {
    nameToNode.set(entity.metadata.name, {
      entity,
      children: [],
      depth: 0,
    });
  }

  const roots: OkrTreeNode[] = [];

  for (const entity of filtered) {
    const node = nameToNode.get(entity.metadata.name)!;
    const parentRef = entity.spec.parentRef;
    if (!parentRef) {
      roots.push(node);
      continue;
    }
    const parentName = parseParentName(parentRef);
    const parentNode = nameToNode.get(parentName);
    if (!parentNode) {
      roots.push(node);
    } else {
      parentNode.children.push(node);
    }
  }

  function assignDepths(node: OkrTreeNode, depth: number) {
    node.depth = depth;
    for (const child of node.children) {
      assignDepths(child, depth + 1);
    }
  }
  for (const root of roots) {
    assignDepths(root, 0);
  }

  const byOwner = new Map<string, OkrTreeNode>();
  for (const node of nameToNode.values()) {
    byOwner.set(ownerGroupSlug(node.entity), node);
  }

  return { roots, byName: nameToNode, byOwner };
}

export function useOkrEntities(): UseOkrEntitiesResult {
  const catalogApi = useApi(catalogApiRef);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [allEntities, setAllEntities] = useState<OkrEntity[]>([]);
  const [quarters, setQuarters] = useState<string[]>([]);
  const [selectedQuarter, setSelectedQuarter] = useState('');
  const [selectedScope, setSelectedScope] = useState<string | undefined>(
    undefined,
  );
  const [groupLeads, setGroupLeads] = useState<Map<string, GroupLead>>(new Map());

  useEffect(() => {
    setLoading(true);
    Promise.all([
      catalogApi.getEntities({ filter: { kind: 'Resource', 'spec.type': 'okr' } }),
      catalogApi.getEntities({ filter: { kind: 'Group' } }),
      catalogApi.getEntities({ filter: { kind: 'User' } }),
    ])
      .then(([{ items: okrItems }, { items: groupItems }, { items: userItems }]) => {
        const okrEntities = okrItems as unknown as OkrEntity[];
        const rawQuarters = [
          ...new Set(
            okrEntities
              .map(e => e.spec.quarter)
              .filter(q => q && !isAnnualQuarter(q)),
          ),
        ];
        const sorted = sortQuarters(rawQuarters);
        setAllEntities(okrEntities);
        setQuarters(sorted);
        setSelectedQuarter(sorted[sorted.length - 1] ?? '');

        // Build user map: user-name → { displayName, picture }
        const userMap = new Map<string, GroupLead>();
        for (const u of userItems) {
          const profile = (u.spec as any)?.profile;
          if (profile?.displayName) {
            userMap.set(u.metadata.name, {
              displayName: profile.displayName,
              picture: profile.picture,
              entityName: u.metadata.name,
            });
          }
        }

        // Build group leads map: group-slug → lead from User entity
        const leads = new Map<string, GroupLead>();
        for (const g of groupItems) {
          const leadRef = g.metadata.annotations?.['team/lead'];
          if (!leadRef) continue;
          const userName = leadRef.split('/').pop();
          if (!userName) continue;
          const lead = userMap.get(userName);
          if (lead) leads.set(g.metadata.name, lead);
        }
        setGroupLeads(leads);
        setLoading(false);
      })
      .catch((err: Error) => {
        setError(err);
        setLoading(false);
      });
  }, [catalogApi]);

  const tree = useMemo(
    () =>
      selectedQuarter && allEntities.length > 0
        ? buildTree(allEntities, selectedQuarter)
        : undefined,
    [allEntities, selectedQuarter],
  );

  const scopeNodes = useMemo(
    () => (tree ? tree.roots.flatMap(root => root.children) : []),
    [tree],
  );

  return {
    loading,
    error,
    entities: allEntities,
    tree,
    quarters,
    selectedQuarter,
    setSelectedQuarter,
    scopeNodes,
    selectedScope,
    setSelectedScope,
    groupLeads,
  };
}
